// Settings > Account (admins only): how many accounts exist up to a chosen
// day, how they sign in, and who signed in recently.
import { useEffect, useState } from "react";
import type { AdminStatsResponse } from "@wp/shared";
import { api } from "../../net/api";

/** Today as YYYY-MM-DD in the viewer's time zone (for the date input). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** End of that local day, in ms. */
function endOfDay(day: string): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

export function AdminStats() {
  const [day, setDay] = useState(todayLocal);
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(d: string) {
    setBusy(true);
    const res = await api.adminStats(Math.min(endOfDay(d), Date.now()));
    setBusy(false);
    if (!res.ok) return setError(res.error.message);
    setError(null);
    setStats(res.value);
  }

  useEffect(() => {
    void load(day);
    // Load once; the date input reloads on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="stack-form group-box admin-stats" data-testid="admin-stats">
      <h3>Admin: player accounts</h3>
      <label>
        As of
        <input
          type="date"
          value={day}
          max={todayLocal()}
          onChange={(e) => {
            setDay(e.target.value);
            if (e.target.value) void load(e.target.value);
          }}
        />
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {busy && !stats && <p className="small">Loading…</p>}
      {stats && (
        <>
          <p>
            <strong>{stats.accounts}</strong> accounts
          </p>
          <ul className="small">
            <li>Website (username and password): {stats.byType.website}</li>
            <li>Discord (website button or the Discord Activity): {stats.byType.discord}</li>
            <li>Google: {stats.byType.google}</li>
          </ul>
          <p className="small">
            Signed in during the last day: {stats.signedIn24h} · last 7 days: {stats.signedIn7d}
          </p>
          <p className="small">New accounts per day (UTC): {stats.newPerDay.map((d) => `${d.day.slice(5)}: ${d.count}`).join(" · ")}</p>
        </>
      )}
    </section>
  );
}
